// src/pages/admin/components/ProgramManagement/ProgramManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { fetchPrograms, saveProgram, deleteProgram } from '../../services/programService';
import AssignmentManagement from '../AssignmentManagement/AssignmentManagement';
import '../AssignmentManagement/AssignmentManagement.css';

const ProgramManagement = ({ groups }) => {
  const [programs, setPrograms] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [isEditingProgram, setIsEditingProgram] = useState(false);
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadPrograms = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchPrograms();
      setPrograms(data);
    } catch (err) {
      console.error("Error loading programs:", err);
      setError("Failed to load programs: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  useEffect(() => {
    let timer;
    if (success || error) {
      timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [success, error]);

  const handleSelectProgram = (program) => {
    setSelectedProgram(program);
    setIsEditingProgram(false);
    setError(null);
    setSuccess(null);
  };

  const handleCreateProgram = () => {
    setSelectedProgram({ id: 'new', title: '', description: '' });
    setFormTitle('');
    setFormDescription('');
    setIsEditingProgram(true);
  };

  const handleEditProgram = (program) => {
    setFormTitle(program.title);
    setFormDescription(program.description || '');
    setIsEditingProgram(true);
  };

  const handleSaveProgram = async () => {
    if (!formTitle.trim()) {
      setError("Program title cannot be empty");
      return;
    }

    try {
      setLoading(true);
      const isNew = !selectedProgram?.id || selectedProgram.id === 'new';
      const savedId = await saveProgram(
        { title: formTitle.trim(), description: formDescription.trim() },
        isNew ? null : selectedProgram.id
      );

      await loadPrograms();

      // Re-select the saved program
      const refreshed = await fetchPrograms();
      const saved = refreshed.find(p => p.id === savedId);
      if (saved) setSelectedProgram(saved);

      setIsEditingProgram(false);
      setSuccess(isNew ? `Program "${formTitle}" created.` : `Program "${formTitle}" updated.`);
    } catch (err) {
      console.error("Error saving program:", err);
      setError("Failed to save program: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProgram = async () => {
    if (!selectedProgram?.id || selectedProgram.id === 'new') return;

    if (!window.confirm(`Delete program "${selectedProgram.title}"? Its assignments will remain but will no longer be linked to a program.`)) return;

    try {
      setLoading(true);
      await deleteProgram(selectedProgram.id);
      setSelectedProgram(null);
      setIsEditingProgram(false);
      await loadPrograms();
      setSuccess("Program deleted.");
    } catch (err) {
      console.error("Error deleting program:", err);
      setError("Failed to delete program: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    if (!selectedProgram?.id || selectedProgram.id === 'new') {
      setSelectedProgram(null);
    }
    setIsEditingProgram(false);
  };

  const handleBackToPrograms = () => {
    setSelectedProgram(null);
    setIsEditingProgram(false);
  };

  if (loading && !programs.length) {
    return <div className="loading">Loading programs...</div>;
  }

  // ── Level 2: assignments for a selected program ───────────────────────────
  if (selectedProgram && selectedProgram.id !== 'new' && !isEditingProgram) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <button className="btn-secondary" onClick={handleBackToPrograms}>
            ← Programs
          </button>
          <h2 style={{ margin: 0 }}>{selectedProgram.title}</h2>
          <button
            className="btn-primary"
            onClick={() => handleEditProgram(selectedProgram)}
            style={{ marginLeft: 'auto' }}
          >
            Edit Program
          </button>
          <button className="btn-danger" onClick={handleDeleteProgram}>
            Delete Program
          </button>
        </div>

        {selectedProgram.description && (
          <p style={{ color: '#666', marginBottom: '20px' }}>{selectedProgram.description}</p>
        )}

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* AssignmentManagement scoped to this program */}
        <AssignmentManagement groups={groups} program={selectedProgram} />
      </div>
    );
  }

  // ── Level 1 or program edit form ─────────────────────────────────────────
  return (
    <div className="assignment-management-container">
      {/* Program edit form */}
      {isEditingProgram && (
        <div style={{
          background: '#f8f9fa',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <h3 style={{ marginTop: 0 }}>
            {selectedProgram?.id === 'new' ? 'Create Program' : `Edit: ${selectedProgram?.title}`}
          </h3>

          <div className="form-group">
            <label htmlFor="program-title">Title</label>
            <input
              id="program-title"
              type="text"
              className="search-input"
              value={formTitle}
              onChange={e => setFormTitle(e.target.value)}
              placeholder="e.g. Spring 2026 Cohort"
              style={{ width: '100%', marginTop: '4px' }}
            />
          </div>

          <div className="form-group" style={{ marginTop: '12px' }}>
            <label htmlFor="program-description">Description (optional)</label>
            <textarea
              id="program-description"
              value={formDescription}
              onChange={e => setFormDescription(e.target.value)}
              placeholder="Brief description of this program..."
              rows={3}
              style={{
                width: '100%',
                marginTop: '4px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          <div className="form-actions" style={{ marginTop: '16px' }}>
            <button className="btn-primary" onClick={handleSaveProgram} disabled={loading}>
              {loading ? 'Saving...' : 'Save Program'}
            </button>
            <button className="btn-secondary" onClick={handleCancelEdit}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Program list */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0 }}>Programs</h2>
        {!isEditingProgram && (
          <button className="btn-primary" onClick={handleCreateProgram}>
            + New Program
          </button>
        )}
      </div>

      {programs.length === 0 ? (
        <div className="no-selection-message">
          <p>No programs yet. Create your first program to get started.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {programs.map(program => (
            <div
              key={program.id}
              onClick={() => handleSelectProgram(program)}
              style={{
                padding: '16px 20px',
                background: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                transition: 'box-shadow 0.2s, border-color 0.2s'
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                e.currentTarget.style.borderColor = '#19a4a0';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.borderColor = '#e0e0e0';
              }}
            >
              <div>
                <div style={{ fontWeight: '500', fontSize: '16px', color: '#333' }}>
                  {program.title}
                </div>
                {program.description && (
                  <div style={{ fontSize: '13px', color: '#666', marginTop: '4px' }}>
                    {program.description}
                  </div>
                )}
              </div>
              <span style={{ color: '#19a4a0', fontSize: '20px' }}>›</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProgramManagement;